import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { InventoryLocation, OfficeOption, RouteOption, fetchRoutes } from './inventoryApi';
import { useT } from '../../../i18n';

interface LocationPickerProps {
  offices: OfficeOption[];
  value: InventoryLocation;
  onChange: (value: { officeId: number | null; routeId: number | null }) => void;
  /** Etiqueta del grupo (p. ej. "Origen", "Destino"). */
  label?: string;
  /** Si es false, solo se puede elegir oficina. */
  allowRoute?: boolean;
  /** Si es false, se exige elegir una ruta. */
  allowOfficeOnly?: boolean;
  /** Oficina fija (p. ej. destino de un traslado = misma oficina del origen). */
  lockedOfficeId?: number | null;
  disabled?: boolean;
  size?: 'small' | 'medium';
}

/**
 * Selector de ubicación de inventario: sede y, opcionalmente, una de sus rutas.
 * Si se elige ruta, la ubicación es la ruta; si no, la sede.
 */
const LocationPicker: React.FC<LocationPickerProps> = ({
  offices,
  value,
  onChange,
  label,
  allowRoute = true,
  allowOfficeOnly = true,
  lockedOfficeId,
  disabled,
  size = 'small',
}) => {
  const t = useT();
  const officeId = lockedOfficeId ?? value.officeId ?? null;
  const [routes, setRoutes] = useState<RouteOption[]>([]);

  useEffect(() => {
    let mounted = true;
    if (!officeId || !allowRoute) {
      setRoutes([]);
      return;
    }
    fetchRoutes(Number(officeId))
      .then((list) => mounted && setRoutes(list))
      .catch(() => mounted && setRoutes([]));
    return () => {
      mounted = false;
    };
  }, [officeId, allowRoute]);

  const prefix = label ? `${label} · ` : '';

  const officeLabel = `${prefix}${t('common.office')}`;
  const routeLabel = `${prefix}${t('common.route')}`;

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: allowRoute ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)' },
      }}
    >
      <TextField
        select
        fullWidth
        size={size}
        label={officeLabel}
        value={officeId ? String(officeId) : ''}
        disabled={disabled || Boolean(lockedOfficeId)}
        onChange={(e) => onChange({ officeId: Number(e.target.value) || null, routeId: null })}
        SelectProps={{ displayEmpty: true }}
        InputLabelProps={{ shrink: true }}
      >
        <MenuItem value="" disabled>
          {t('inventory.location.chooseOffice')}
        </MenuItem>
        {offices.map((o) => (
          <MenuItem key={o.id} value={String(o.id)}>
            {o.name}
          </MenuItem>
        ))}
      </TextField>
      {allowRoute && (
        <TextField
          select
          fullWidth
          size={size}
          label={routeLabel}
          value={value.routeId ? String(value.routeId) : ''}
          disabled={disabled || !officeId}
          onChange={(e) => onChange({ officeId: officeId, routeId: Number(e.target.value) || null })}
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
        >
          {allowOfficeOnly ? (
            <MenuItem value="">{t('inventory.location.officeOnly')}</MenuItem>
          ) : (
            <MenuItem value="" disabled>
              {t('inventory.location.chooseRoute')}
            </MenuItem>
          )}
          {routes.map((c) => (
            <MenuItem key={c.id} value={String(c.id)}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>
      )}
    </Box>
  );
};

export default LocationPicker;

/** Ubicación efectiva para el API: ruta si hay, si no oficina. */
export const toApiLocation = (value: { officeId?: number | null; routeId?: number | null }): InventoryLocation =>
  value.routeId ? { routeId: value.routeId } : { officeId: value.officeId ?? null };

export const isLocationComplete = (
  value: { officeId?: number | null; routeId?: number | null },
  requireRoute = false,
) => (requireRoute ? Boolean(value.routeId) : Boolean(value.officeId || value.routeId));
